import { useState, useEffect, useRef } from 'react';

export default function Automation() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const automationFeatures = [
    {
      icon: 'ri-shopping-cart-line',
      title: 'Tự động hóa bán hàng',
      description: 'Tự động phân bổ lead, gửi email follow-up, cập nhật trạng thái deal và nhắc nhở sale',
      stats: '75% tiết kiệm thời gian',
      color: 'from-[#06b6d4] to-[#00c897]'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Marketing Automation',
      description: 'Chiến dịch email tự động, phân khúc khách hàng, lead scoring và nurturing campaign',
      stats: '3x tăng conversion',
      color: 'from-[#2a4b7c] to-[#3d5a80]'
    },
    {
      icon: 'ri-task-line',
      title: 'Quản lý công việc tự động',
      description: 'Tạo task tự động, phân công theo quy tắc, theo dõi tiến độ và báo cáo tự động',
      stats: '60% giảm công việc thủ công',
      color: 'from-[#2ecc71] to-[#00c897]'
    }
  ];

  const dataCollection = [
    {
      icon: 'ri-global-line',
      title: 'Thu thập đa nguồn',
      description: 'Website, social media, form, landing page, chatbot'
    },
    {
      icon: 'ri-filter-3-line',
      title: 'Chuẩn hóa tự động',
      description: 'Làm sạch, phân loại và làm giàu dữ liệu'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Kho dữ liệu liên tục',
      description: 'Xây dựng database khách hàng tiềm năng phong phú'
    }
  ];

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Automation Section */}
        <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#2ecc71]/10 text-[#2ecc71] text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <i className="ri-flashlight-line"></i>
            <span>TỰ ĐỘNG HÓA</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Tự động hóa toàn bộ quy trình kinh doanh
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Giảm phụ thuộc vào thao tác thủ công, tăng hiệu suất và đảm bảo tính nhất quán trong mọi hoạt động
          </p>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-12 sm:mb-16 md:mb-24">
          {automationFeatures.map((feature, index) => (
            <div key={index} className={`bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100 hover:border-[#06b6d4]/30 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: `${200 + index * 100}ms` }}>
              <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} mb-4 sm:mb-5 md:mb-6`}>
                <i className={`${feature.icon} text-2xl sm:text-2xl md:text-3xl text-white`}></i>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{feature.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">{feature.description}</p>
              <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-[#2ecc71]/10 text-[#2ecc71] font-semibold text-xs sm:text-sm">
                <i className="ri-arrow-up-line"></i>
                <span>{feature.stats}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Data Collection Section */}
        <div className={`bg-gradient-to-r from-[#2a4b7c] to-[#3d5a80] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <i className="ri-database-line"></i>
              <span>THU THẬP DỮ LIỆU</span>
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
              Tự động thu thập dữ liệu khách hàng tiềm năng
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto px-4">
              Xây dựng kho dữ liệu khách hàng liên tục cho bán hàng và marketing
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {dataCollection.map((item, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/20 transition-all ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`} style={{ transitionDelay: `${700 + index * 150}ms` }}>
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-lg bg-white/10 mb-3 sm:mb-4">
                  <i className={`${item.icon} text-2xl sm:text-3xl text-[#00c897]`}></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{item.title}</h4>
                <p className="text-white/80 text-xs sm:text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 sm:mt-10 md:mt-12 text-center">
            <button className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#2a4b7c] rounded-lg font-semibold hover:shadow-xl transition-all inline-flex items-center space-x-2 text-sm sm:text-base cursor-pointer">
              <span>Tìm hiểu thêm về Data Collection</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
